import { useState, useEffect } from 'react'

/**
 * Debounce a value
 * @param {any} value - The value to debounce
 * @param {number} delay - The delay in milliseconds
 * @returns {any} The debounced value
 * 
 * @example
import { useQuery } from '@tanstack/react-query'
import useDebounce from '@hook/useDebounce'

const SearchComponent = () => {
    
    const [searchTerm, setSearchTerm] = useState('')
    const debouncedSearchTerm = useDebounce(searchTerm, 500) // Delay by 500ms
    
    const { data, isLoading, error } = useQuery(
        ['search', debouncedSearchTerm], // Query key includes the debounced term
        async ({ signal }) => {
            const response = await axios.get(`/api/search?query=${debouncedSearchTerm}`, {
            signal, // Pass AbortSignal for cancellation
        })
        return response.data
    },
    {
        enabled: !!debouncedSearchTerm, // Only fetch if there's input
    })
    
    return (
        <div>
            <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..." />
            {isLoading && <p>Loading...</p>}
            {error && <p>Error: {error.message}</p>}
            {data && <ul>{data.map(item => <li key={item.id}>{item.name}</li>)}</ul>}
        </div>
    )
    
}
 */
const useDebounce = (value, delay) => {
    
    const [debouncedValue, setDebouncedValue] = useState(value)
    
    useEffect(() => {
        
        const handler = setTimeout(() => {
            setDebouncedValue(value)
        }, delay)
        
        return () => {
            clearTimeout(handler) // Cleanup previous timeout
        }
        
    }, [value, delay])
    
    return debouncedValue
    
}

export default useDebounce
